/**
 * Edit Profile Page
 * Renders different profile edit forms based on user type (homeowner, designer, contractor)
 */

import { DESIGNER_BIO_MAX_LENGTH, isPincode } from "@shared/types";
import { ArrowLeft, Save } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
	MultiImageUpload,
	SingleImageUpload,
} from "@/components/ui/image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ToggleChip, ToggleChipGroup } from "@/components/ui/toggle-chip";
import { useAuthStore } from "@/features/auth/store/auth.store";
import type { PreviousProject } from "@/features/marketplace/services/marketplace-api.service";
import {
	BudgetRangeFields,
	type BudgetRangeValue,
	budgetErrorKey,
	budgetFromRupees,
	budgetPayload,
	LocationFields,
	type LocationValue,
	locationPayload,
	OptionChips,
	PincodeInput,
	parseServiceRadius,
} from "@/features/onboarding/components/fields";
import {
	contractorProjectTypeOptions,
	designerProjectTypeOptions,
	designerServiceOptions,
	designStyleOptions,
	goalOptions,
	realtorClientTypeOptions,
	realtorPropertyTypeOptions,
} from "@/features/onboarding/options";
import {
	useContractorProfile,
	useDesignerProfile,
	useHomeownerProfile,
	useRealtorProfile,
	useUpdateContractorProfile,
	useUpdateDesignerProfile,
	useUpdateHomeownerProfile,
	useUpdateRealtorProfile,
} from "@/features/settings/hooks/use-profile";
import type {
	updateContractorProfile,
	updateDesignerProfile,
} from "@/features/settings/services/settings-api.service";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { useServiceCities, useTrades } from "@/lib/constants/markets";
import { analytics } from "@/services/analytics.service";
import { PreviousProjectsEditor } from "../components/previous-projects-editor";

// ============================================================================
// HELPERS
// ============================================================================

function toggleItem(list: string[], item: string): string[] {
	return list.includes(item) ? list.filter((i) => i !== item) : [...list, item];
}

/** A stored location as the location inputs hold it. */
function locationValue(profile: {
	city?: string | null;
	state?: string | null;
	pincode?: string | null;
}): LocationValue {
	return {
		city: profile.city ?? "",
		state: profile.state ?? "",
		pincode: profile.pincode ?? "",
	};
}

/** The "Other" text to send: kept only while "other" is chosen. */
function otherTextFor(list: readonly string[], text: string): string | null {
	return list.includes("other") ? text.trim() || null : null;
}

/** Keys of `after` whose value differs from `before`, compared as JSON. */
function changedFieldNames<T extends object>(before: T, after: T): string[] {
	return (Object.keys(after) as (keyof T & string)[]).filter(
		(key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
	);
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

/** The non-null data a profile hook resolves to. */
type Loaded<H extends (...args: never[]) => { data: unknown }> = NonNullable<
	ReturnType<H>["data"]
>;

function FormSkeleton() {
	return (
		<FloatPanel elevation={1} padding={28}>
			<div className="space-y-6">
				<div className="space-y-1">
					<Skeleton className="h-7 w-48" />
					<Skeleton className="mt-2 h-5 w-72" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-20" />
					<Skeleton className="h-10 w-full" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-24" />
					<Skeleton className="h-10 w-full" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-28" />
					<Skeleton className="h-10 w-full" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-32" />
					<div className="flex flex-wrap gap-2">
						<Skeleton className="h-9 w-24 rounded-full" />
						<Skeleton className="h-9 w-28 rounded-full" />
						<Skeleton className="h-9 w-20 rounded-full" />
						<Skeleton className="h-9 w-32 rounded-full" />
						<Skeleton className="h-9 w-24 rounded-full" />
					</div>
				</div>
				<Skeleton className="h-12 w-full rounded-xl" />
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// HOMEOWNER EDIT FORM
// ============================================================================

function HomeownerEditForm() {
	const {
		data: profile,
		isPending,
		isFetching,
		refetch,
	} = useHomeownerProfile();

	/*
	 * The form below is keyed on the profile, so React builds it once the data
	 * exists and rebuilds it if a different profile arrives. That replaces an
	 * effect that copied the profile into a dozen state slots and a ref that
	 * stopped it re-running -- machinery whose only job was to imitate a mount.
	 */
	if (isPending) return <FormSkeleton />;
	/*
	 * A profile that did not load is NOT a slow one.
	 *
	 * This getter `catch { return null; }`es, so a failed request resolves
	 * SUCCESSFULLY with null: `isError` never becomes true and `isPending` goes
	 * false. Folding that into the skeleton -- which the first version of this
	 * split did -- left the screen spinning forever, with `refetchOnWindowFocus`
	 * off and a five-minute staleTime meaning nothing would come along to end
	 * it. The base code rendered an empty editable form instead, a different
	 * wrong answer: it invites the user to retype a profile that already exists
	 * and save over it.
	 *
	 * A retry is the honest third option. The durable fix is upstream -- this
	 * getter should rethrow on anything but a 404, so `isError` can mean
	 * something, the way `fetchDesignerProfile` now does for the designer form.
	 */
	if (!profile) {
		return (
			<QueryErrorState onRetry={() => refetch()} isRetrying={isFetching} />
		);
	}
	return <HomeownerEditFormLoaded key={profile.id} profile={profile} />;
}

function HomeownerEditFormLoaded({
	profile,
}: {
	profile: Loaded<typeof useHomeownerProfile>;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { refreshUser: refreshAuthUser } = useAuthStore();
	const updateProfile = useUpdateHomeownerProfile();
	const [error, setError] = useState<string | null>(null);
	const CITIES = useServiceCities().map((c) => c.value);

	const [name, setName] = useState(() => profile.name || "");
	const [email, setEmail] = useState(() => profile.email || "");
	const [city, setCity] = useState(() => profile.city || "");
	const [locality, setLocality] = useState(() => profile.locality || "");
	const [pincode, setPincode] = useState(() => profile.pincode || "");
	const [goals, setGoals] = useState<string[]>(() => profile.goals || []);
	const [profilePictureUrl, setProfilePictureUrl] = useState(
		() => profile.profilePictureUrl || "",
	);

	const isSaving = updateProfile.isPending;

	const handleSave = async () => {
		setError(null);
		if (pincode && !isPincode(pincode)) {
			setError(t("onboarding.fields.pincodeInvalid"));
			return;
		}
		try {
			const result = await updateProfile.mutateAsync({
				name: name.trim(),
				email: email.trim() || undefined,
				city: city || undefined,
				locality: locality.trim() || undefined,
				pincode: pincode || null,
				goals,
				profilePictureUrl: profilePictureUrl || undefined,
			});
			if (result.success) {
				await refreshAuthUser();
				navigate(-1);
			} else {
				setError(result.error || t("profile.failedToSave"));
			}
		} catch {
			setError(t("profile.somethingWentWrong"));
		}
	};

	return (
		<FloatPanel elevation={1} padding={28}>
			<div className="space-y-6">
				{/* Header */}
				<div className="space-y-1">
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 600,
							fontSize: 20,
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("profile.editProfile")}
					</h2>
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-muted)",
							fontFamily: "var(--font-body)",
						}}
					>
						{t("profile.homeownerSubtitle")}
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="name">{t("profile.name")}</Label>
					<Input
						id="name"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("profile.namePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.profilePhoto")}</Label>
					<SingleImageUpload
						currentUrl={profilePictureUrl || undefined}
						onUpload={(url) => setProfilePictureUrl(url)}
						onRemove={() => setProfilePictureUrl("")}
						enableCrop
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="email">{t("profile.email")}</Label>
					<Input
						id="email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder={t("profile.emailPlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="city">{t("profile.city")}</Label>
					<select
						id="city"
						value={city}
						onChange={(e) => setCity(e.target.value)}
						style={{
							display: "flex",
							height: 36,
							width: "100%",
							borderRadius: 6,
							border: "1.5px solid var(--color-border-default)",
							background: "var(--color-dashboard-surface)",
							paddingLeft: 12,
							paddingRight: 12,
							fontSize: 14,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					>
						<option value="">{t("profile.selectCity")}</option>
						{CITIES.map((c) => (
							<option key={c} value={c}>
								{c}
							</option>
						))}
					</select>
				</div>

				<div className="space-y-2">
					<Label htmlFor="locality">{t("profile.locality")}</Label>
					<Input
						id="locality"
						type="text"
						value={locality}
						onChange={(e) => setLocality(e.target.value)}
						placeholder={t("profile.localityPlaceholder")}
					/>
				</div>

				<PincodeInput value={pincode} onChange={setPincode} />

				<OptionChips
					label={t("onboarding.homeowner.goalsStepTitle")}
					options={goalOptions(t)}
					selected={goals}
					onChange={setGoals}
				/>

				{error && (
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-pill-risk)",
							fontFamily: "var(--font-body)",
						}}
					>
						{error}
					</p>
				)}

				<StudioButton
					onClick={handleSave}
					variant="primary"
					disabled={isSaving}
					icon={Save}
					style={{ width: "100%" }}
				>
					{isSaving ? t("common.saving") : t("common.saveChanges")}
				</StudioButton>
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// DESIGNER EDIT FORM
// ============================================================================

function DesignerEditForm() {
	const {
		data: profile,
		isPending,
		isFetching,
		refetch,
	} = useDesignerProfile();

	/*
	 * The form below is keyed on the profile, so React builds it once the data
	 * exists and rebuilds it if a different profile arrives. That replaces an
	 * effect that copied the profile into a dozen state slots and a ref that
	 * stopped it re-running -- machinery whose only job was to imitate a mount.
	 */
	if (isPending) return <FormSkeleton />;
	/*
	 * A profile that did not load is NOT a slow one.
	 *
	 * `useDesignerProfile` reads through `fetchDesignerProfile`, which rethrows
	 * anything but a 404, so a broken request lands here with `isError` true
	 * and `data` undefined, and a user who has not onboarded lands here with
	 * `data` null. Both are `!profile`, and both want the same thing from this
	 * screen: a retry rather than the skeleton (which would spin forever, with
	 * `refetchOnWindowFocus` off and a five-minute staleTime) and rather than
	 * an empty editable form (which invites the user to retype a profile that
	 * may already exist and save over it).
	 */
	if (!profile) {
		return (
			<QueryErrorState onRetry={() => refetch()} isRetrying={isFetching} />
		);
	}
	return <DesignerEditFormLoaded key={profile.id} profile={profile} />;
}

/** The designer form's editable values, as the inputs hold them. */
interface DesignerFormValues {
	name: string;
	firmName: string;
	bio: string;
	experienceYears: string;
	services: string[];
	styles: string[];
	serviceCities: string[];
	budget: BudgetRangeValue;
	portfolioImages: string[];
	profilePictureUrl: string;
	previousProjects: PreviousProject[];
	location: LocationValue;
	projectTypes: string[];
	projectTypesOther: string;
}

function designerFormValues(
	profile: Loaded<typeof useDesignerProfile>,
): DesignerFormValues {
	return {
		name: profile.name || "",
		firmName: profile.firmName || "",
		bio: profile.bio || "",
		experienceYears:
			profile.experienceYears != null ? String(profile.experienceYears) : "",
		services: profile.services || [],
		styles: profile.styles || [],
		serviceCities: profile.serviceCities || [],
		budget: budgetFromRupees(profile.priceRangeMin, profile.priceRangeMax),
		portfolioImages: profile.portfolioImages || [],
		profilePictureUrl: profile.profilePictureUrl || "",
		previousProjects: profile.previousProjects || [],
		location: locationValue(profile),
		projectTypes: profile.projectTypes || [],
		projectTypesOther: profile.projectTypesOther || "",
	};
}

/**
 * The update request the designer form sends for a set of form values.
 *
 * `portfolioImages` is sent only when it differs from `initial`: the backend
 * treats a present `portfolioImages` as "replace the list", so an edit that
 * did not touch the images leaves them alone. No `length > 0` guard: removing
 * every image is a real edit, and the backend accepts an empty list.
 *
 * `bio` is likewise sent only when it changed. The backend caps a designer
 * bio at 300 characters (#362); a longer bio saved before the cap would
 * otherwise make every later save fail, whatever field was edited.
 */
function toDesignerUpdate(
	values: DesignerFormValues,
	initial: DesignerFormValues,
): Parameters<typeof updateDesignerProfile>[0] {
	const portfolioImagesChanged =
		JSON.stringify(values.portfolioImages) !==
		JSON.stringify(initial.portfolioImages);

	return {
		name: values.name.trim(),
		firmName: values.firmName.trim() || undefined,
		// An emptied bio is sent as "", which the backend stores as null.
		bio:
			values.bio.trim() === initial.bio.trim() ? undefined : values.bio.trim(),
		experienceYears: values.experienceYears
			? parseInt(values.experienceYears, 10)
			: undefined,
		services: values.services.length > 0 ? values.services : undefined,
		styles: values.styles.length > 0 ? values.styles : undefined,
		serviceCities:
			values.serviceCities.length > 0 ? values.serviceCities : undefined,
		...budgetPayload(values.budget),
		portfolioImages: portfolioImagesChanged
			? values.portfolioImages
			: undefined,
		profilePictureUrl: values.profilePictureUrl || undefined,
		previousProjects: values.previousProjects,
		...locationPayload(values.location),
		projectTypes: values.projectTypes,
		projectTypesOther: otherTextFor(
			values.projectTypes,
			values.projectTypesOther,
		),
	};
}

function DesignerEditFormLoaded({
	profile,
}: {
	profile: Loaded<typeof useDesignerProfile>;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { refreshUser: refreshAuthUser } = useAuthStore();
	const updateProfile = useUpdateDesignerProfile();
	const [error, setError] = useState<string | null>(null);
	const CITIES = useServiceCities().map((c) => c.value);

	const [initial] = useState(() => designerFormValues(profile));
	const [name, setName] = useState(initial.name);
	const [firmName, setFirmName] = useState(initial.firmName);
	const [bio, setBio] = useState(initial.bio);
	const [experienceYears, setExperienceYears] = useState(
		initial.experienceYears,
	);
	const [services, setServices] = useState<string[]>(initial.services);
	const [styles, setStyles] = useState<string[]>(initial.styles);
	const [serviceCities, setServiceCities] = useState<string[]>(
		initial.serviceCities,
	);
	const [budget, setBudget] = useState(initial.budget);
	const [portfolioImages, setPortfolioImages] = useState<string[]>(
		initial.portfolioImages,
	);
	const [profilePictureUrl, setProfilePictureUrl] = useState(
		initial.profilePictureUrl,
	);
	const [previousProjects, setPreviousProjects] = useState<PreviousProject[]>(
		initial.previousProjects,
	);
	const [location, setLocation] = useState(initial.location);
	const [projectTypes, setProjectTypes] = useState<string[]>(
		initial.projectTypes,
	);
	const [projectTypesOther, setProjectTypesOther] = useState(
		initial.projectTypesOther,
	);

	const isSaving = updateProfile.isPending;

	const handleSave = async () => {
		setError(null);
		if (location.pincode && !isPincode(location.pincode)) {
			setError(t("onboarding.fields.pincodeInvalid"));
			return;
		}
		const budgetError = budgetErrorKey(budget);
		if (budgetError) {
			setError(t(budgetError));
			return;
		}
		try {
			const update = toDesignerUpdate(
				{
					name,
					firmName,
					bio,
					experienceYears,
					services,
					styles,
					serviceCities,
					budget,
					portfolioImages,
					profilePictureUrl,
					previousProjects,
					location,
					projectTypes,
					projectTypesOther,
				},
				initial,
			);
			const result = await updateProfile.mutateAsync(update);
			if (result.success) {
				// Field names only, never their values.
				const fields = changedFieldNames(
					toDesignerUpdate(initial, initial),
					update,
				);
				if (fields.length > 0) {
					analytics.profile.updated({ userType: "designer", fields });
				}
				await refreshAuthUser();
				navigate(-1);
			} else {
				setError(result.error || t("profile.failedToSave"));
			}
		} catch {
			setError(t("profile.somethingWentWrong"));
		}
	};

	return (
		<FloatPanel elevation={1} padding={28}>
			<div className="space-y-6">
				<div className="border-b border-[var(--color-dashboard-border)] pb-4">
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 600,
							fontSize: 20,
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("profile.editProfile")}
					</h2>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13.5,
							color: "var(--color-dashboard-muted)",
							marginTop: 4,
						}}
					>
						{t("profile.designerSubtitle")}
					</p>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						{t("profile.name")}
					</span>
					<input
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("profile.namePlaceholder")}
						style={{
							width: "100%",
							padding: "10px 14px",
							border: "1px solid var(--color-border-default)",
							borderRadius: 7,
							background: "var(--color-dashboard-paper)",
							fontSize: 14,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						{t("profile.profilePhoto")}
					</span>
					<SingleImageUpload
						currentUrl={profilePictureUrl || undefined}
						onUpload={(url) => setProfilePictureUrl(url)}
						onRemove={() => setProfilePictureUrl("")}
						enableCrop
					/>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						{t("profile.firmName")}
					</span>
					<input
						type="text"
						value={firmName}
						onChange={(e) => setFirmName(e.target.value)}
						placeholder={t("profile.firmNamePlaceholder")}
						style={{
							width: "100%",
							padding: "10px 14px",
							border: "1px solid var(--color-border-default)",
							borderRadius: 7,
							background: "var(--color-dashboard-paper)",
							fontSize: 14,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						{t("profile.bio")}
					</span>
					<textarea
						value={bio}
						maxLength={DESIGNER_BIO_MAX_LENGTH}
						onChange={(e) => setBio(e.target.value)}
						placeholder={t("profile.designerBioPlaceholder")}
						rows={4}
						style={{
							width: "100%",
							padding: "10px 14px",
							border: "1px solid var(--color-border-default)",
							borderRadius: 7,
							background: "var(--color-dashboard-paper)",
							fontSize: 14,
							color: "var(--color-dashboard-text)",
							outline: "none",
							resize: "vertical",
						}}
					/>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						{t("profile.experienceYears")}
					</span>
					<input
						type="number"
						value={experienceYears}
						onChange={(e) => setExperienceYears(e.target.value)}
						placeholder="e.g., 5"
						min={0}
						style={{
							width: "100%",
							padding: "10px 14px",
							border: "1px solid var(--color-border-default)",
							borderRadius: 7,
							background: "var(--color-dashboard-paper)",
							fontSize: 14,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
							marginBottom: 6,
						}}
					>
						{t("common.services")}
					</span>
					<ToggleChipGroup>
						{designerServiceOptions(t, services).map((service) => (
							<ToggleChip
								key={service.value}
								selected={services.includes(service.value)}
								onClick={() =>
									setServices((prev) => toggleItem(prev, service.value))
								}
							>
								{service.label}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
							marginBottom: 6,
						}}
					>
						{t("profile.designStyles")}
					</span>
					<ToggleChipGroup>
						{designStyleOptions(t, styles).map((style) => (
							<ToggleChip
								key={style.value}
								selected={styles.includes(style.value)}
								onClick={() =>
									setStyles((prev) => toggleItem(prev, style.value))
								}
							>
								{style.label}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
							marginBottom: 6,
						}}
					>
						{t("profile.serviceCities")}
					</span>
					<ToggleChipGroup>
						{CITIES.map((c) => (
							<ToggleChip
								key={c}
								selected={serviceCities.includes(c)}
								onClick={() => setServiceCities((prev) => toggleItem(prev, c))}
							>
								{c}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<BudgetRangeFields
					label={t("onboarding.designer.budgetQuestion")}
					value={budget}
					onChange={setBudget}
				/>

				<div className="space-y-2">
					<span
						style={{
							display: "block",
							fontSize: 13,
							fontWeight: 500,
							color: "var(--color-dashboard-muted-dark)",
							marginBottom: 6,
						}}
					>
						{t("profile.portfolioImages")}
					</span>
					<MultiImageUpload
						currentUrls={portfolioImages}
						maxImages={10}
						onUpload={(urls) =>
							setPortfolioImages((prev) => [...prev, ...urls])
						}
						onRemove={(index) =>
							setPortfolioImages((prev) => prev.filter((_, i) => i !== index))
						}
					/>
				</div>

				<LocationFields
					label={t("onboarding.designer.location")}
					value={location}
					onChange={setLocation}
				/>

				<OptionChips
					label={t("onboarding.designer.projectTypes")}
					options={designerProjectTypeOptions(t)}
					selected={projectTypes}
					onChange={setProjectTypes}
					otherValue="other"
					otherText={projectTypesOther}
					onOtherTextChange={setProjectTypesOther}
				/>

				<PreviousProjectsEditor
					value={previousProjects}
					onChange={setPreviousProjects}
					profileType="designer"
				/>

				{error && <p className="text-sm text-dashboard-pill-risk">{error}</p>}

				<div className="pt-4 border-t border-[var(--color-dashboard-border)]">
					<StudioButton
						onClick={handleSave}
						variant="primary"
						disabled={isSaving}
						icon={Save}
						style={{ width: "100%" }}
					>
						{isSaving ? t("common.saving") : t("common.saveChanges")}
					</StudioButton>
				</div>
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// CONTRACTOR EDIT FORM
// ============================================================================

function ContractorEditForm() {
	const {
		data: profile,
		isPending,
		isFetching,
		refetch,
	} = useContractorProfile();

	/*
	 * The form below is keyed on the profile, so React builds it once the data
	 * exists and rebuilds it if a different profile arrives. That replaces an
	 * effect that copied the profile into a dozen state slots and a ref that
	 * stopped it re-running -- machinery whose only job was to imitate a mount.
	 */
	if (isPending) return <FormSkeleton />;
	/*
	 * A profile that did not load is NOT a slow one.
	 *
	 * This getter `catch { return null; }`es, so a failed request resolves
	 * SUCCESSFULLY with null: `isError` never becomes true and `isPending` goes
	 * false. Folding that into the skeleton -- which the first version of this
	 * split did -- left the screen spinning forever, with `refetchOnWindowFocus`
	 * off and a five-minute staleTime meaning nothing would come along to end
	 * it. The base code rendered an empty editable form instead, a different
	 * wrong answer: it invites the user to retype a profile that already exists
	 * and save over it.
	 *
	 * A retry is the honest third option. The durable fix is upstream -- this
	 * getter should rethrow on anything but a 404, so `isError` can mean
	 * something, the way `fetchDesignerProfile` now does for the designer form.
	 */
	if (!profile) {
		return (
			<QueryErrorState onRetry={() => refetch()} isRetrying={isFetching} />
		);
	}
	return <ContractorEditFormLoaded key={profile.id} profile={profile} />;
}

/** The contractor form's editable values, as the inputs hold them. */
interface ContractorFormValues {
	name: string;
	bio: string;
	experienceYears: string;
	trades: string[];
	serviceAreas: string[];
	workPhotos: string[];
	profilePictureUrl: string;
	previousProjects: PreviousProject[];
	businessName: string;
	location: LocationValue;
	radiusKm: string;
	projectTypes: string[];
	projectTypesOther: string;
	budget: BudgetRangeValue;
}

function contractorFormValues(
	profile: Loaded<typeof useContractorProfile>,
): ContractorFormValues {
	return {
		name: profile.name || "",
		bio: profile.bio || "",
		experienceYears:
			profile.experienceYears != null ? String(profile.experienceYears) : "",
		trades: profile.trades || [],
		serviceAreas: profile.serviceAreas || [],
		workPhotos: profile.workPhotos || [],
		profilePictureUrl: profile.profilePictureUrl || "",
		previousProjects: profile.previousProjects || [],
		businessName: profile.businessName || "",
		location: locationValue(profile),
		radiusKm:
			profile.serviceRadiusKm != null ? String(profile.serviceRadiusKm) : "",
		projectTypes: profile.projectTypes || [],
		projectTypesOther: profile.projectTypesOther || "",
		budget: budgetFromRupees(profile.priceRangeMin, profile.priceRangeMax),
	};
}

/** The update request the contractor form sends for a set of form values. */
function toContractorUpdate(
	values: ContractorFormValues,
): Parameters<typeof updateContractorProfile>[0] {
	return {
		name: values.name.trim(),
		businessName: values.businessName.trim() || null,
		...locationPayload(values.location),
		serviceRadiusKm: parseServiceRadius(values.radiusKm),
		projectTypes: values.projectTypes,
		projectTypesOther: otherTextFor(
			values.projectTypes,
			values.projectTypesOther,
		),
		...budgetPayload(values.budget),
		bio: values.bio.trim() || undefined,
		experienceYears: values.experienceYears
			? parseInt(values.experienceYears, 10)
			: undefined,
		trades: values.trades.length > 0 ? values.trades : undefined,
		serviceAreas:
			values.serviceAreas.length > 0 ? values.serviceAreas : undefined,
		workPhotos: values.workPhotos,
		profilePictureUrl: values.profilePictureUrl || undefined,
		previousProjects: values.previousProjects,
	};
}

function ContractorEditFormLoaded({
	profile,
}: {
	profile: Loaded<typeof useContractorProfile>;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { refreshUser: refreshAuthUser } = useAuthStore();
	const updateProfile = useUpdateContractorProfile();
	const [error, setError] = useState<string | null>(null);
	const CITIES = useServiceCities().map((c) => c.value);
	const CONTRACTOR_TRADES = useTrades();

	const [initial] = useState(() => contractorFormValues(profile));
	const [name, setName] = useState(initial.name);
	const [bio, setBio] = useState(initial.bio);
	const [experienceYears, setExperienceYears] = useState(
		initial.experienceYears,
	);
	const [trades, setTrades] = useState<string[]>(initial.trades);
	const [serviceAreas, setServiceAreas] = useState<string[]>(
		initial.serviceAreas,
	);
	const [workPhotos, setWorkPhotos] = useState<string[]>(initial.workPhotos);
	const [profilePictureUrl, setProfilePictureUrl] = useState(
		initial.profilePictureUrl,
	);
	const [previousProjects, setPreviousProjects] = useState<PreviousProject[]>(
		initial.previousProjects,
	);
	const [businessName, setBusinessName] = useState(initial.businessName);
	const [location, setLocation] = useState(initial.location);
	const [radiusKm, setRadiusKm] = useState(initial.radiusKm);
	const [projectTypes, setProjectTypes] = useState<string[]>(
		initial.projectTypes,
	);
	const [projectTypesOther, setProjectTypesOther] = useState(
		initial.projectTypesOther,
	);
	const [budget, setBudget] = useState(initial.budget);

	const isSaving = updateProfile.isPending;

	const handleSave = async () => {
		setError(null);
		if (location.pincode && !isPincode(location.pincode)) {
			setError(t("onboarding.fields.pincodeInvalid"));
			return;
		}
		const radius = parseServiceRadius(radiusKm);
		if (Number.isNaN(radius)) {
			setError(t("onboarding.contractor.radiusInvalid"));
			return;
		}
		const budgetError = budgetErrorKey(budget);
		if (budgetError) {
			setError(t(budgetError));
			return;
		}
		try {
			const update = toContractorUpdate({
				name,
				bio,
				experienceYears,
				trades,
				serviceAreas,
				workPhotos,
				profilePictureUrl,
				previousProjects,
				businessName,
				location,
				radiusKm,
				projectTypes,
				projectTypesOther,
				budget,
			});
			const result = await updateProfile.mutateAsync(update);
			if (result.success) {
				// Field names only, never their values.
				const fields = changedFieldNames(toContractorUpdate(initial), update);
				if (fields.length > 0) {
					analytics.profile.updated({ userType: "contractor", fields });
				}
				await refreshAuthUser();
				navigate(-1);
			} else {
				setError(result.error || t("profile.failedToSave"));
			}
		} catch {
			setError(t("profile.somethingWentWrong"));
		}
	};

	return (
		<FloatPanel elevation={1} padding={28}>
			<div className="space-y-6">
				{/* Header */}
				<div className="space-y-1">
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 600,
							fontSize: 20,
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("profile.editProfile")}
					</h2>
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-muted)",
							fontFamily: "var(--font-body)",
						}}
					>
						{t("profile.contractorSubtitle")}
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="contractor-name">{t("profile.name")}</Label>
					<Input
						id="contractor-name"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("profile.namePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="contractor-business-name">
						{t("onboarding.contractor.businessName")}
					</Label>
					<Input
						id="contractor-business-name"
						type="text"
						value={businessName}
						maxLength={200}
						onChange={(e) => setBusinessName(e.target.value)}
						placeholder={t("onboarding.contractor.businessNamePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.profilePhoto")}</Label>
					<SingleImageUpload
						currentUrl={profilePictureUrl || undefined}
						onUpload={(url) => setProfilePictureUrl(url)}
						onRemove={() => setProfilePictureUrl("")}
						enableCrop
					/>
				</div>

				<LocationFields
					label={t("onboarding.contractor.location")}
					value={location}
					onChange={setLocation}
				/>

				<div className="space-y-2">
					<Label htmlFor="contractor-bio">{t("profile.bio")}</Label>
					<Textarea
						id="contractor-bio"
						value={bio}
						onChange={(e) => setBio(e.target.value)}
						placeholder={t("profile.contractorBioPlaceholder")}
						rows={4}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="contractor-experience">
						{t("profile.experienceYears")}
					</Label>
					<Input
						id="contractor-experience"
						type="number"
						value={experienceYears}
						onChange={(e) => setExperienceYears(e.target.value)}
						placeholder="e.g., 8"
						min={0}
					/>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.trades")}</Label>
					<ToggleChipGroup>
						{CONTRACTOR_TRADES.map((trade) => (
							<ToggleChip
								key={trade.value}
								selected={trades.includes(trade.value)}
								onClick={() =>
									setTrades((prev) => toggleItem(prev, trade.value))
								}
							>
								{trade.displayLabel}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.serviceAreas")}</Label>
					<ToggleChipGroup>
						{CITIES.map((c) => (
							<ToggleChip
								key={c}
								selected={serviceAreas.includes(c)}
								onClick={() => setServiceAreas((prev) => toggleItem(prev, c))}
							>
								{c}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<div className="space-y-2">
					<Label htmlFor="contractor-radius">
						{t("onboarding.contractor.radius")}
					</Label>
					<Input
						id="contractor-radius"
						type="number"
						inputMode="numeric"
						value={radiusKm}
						onChange={(e) => setRadiusKm(e.target.value)}
						placeholder={t("onboarding.contractor.radiusPlaceholder")}
					/>
				</div>

				<OptionChips
					label={t("onboarding.contractor.projectTypes")}
					options={contractorProjectTypeOptions(t)}
					selected={projectTypes}
					onChange={setProjectTypes}
					otherValue="other"
					otherText={projectTypesOther}
					onOtherTextChange={setProjectTypesOther}
				/>

				<BudgetRangeFields
					label={t("onboarding.contractor.budget")}
					value={budget}
					onChange={setBudget}
				/>

				<div className="space-y-2">
					<Label>{t("profile.workPhotos")}</Label>
					<MultiImageUpload
						currentUrls={workPhotos}
						maxImages={10}
						onUpload={(urls) => setWorkPhotos((prev) => [...prev, ...urls])}
						onRemove={(index) =>
							setWorkPhotos((prev) => prev.filter((_, i) => i !== index))
						}
					/>
				</div>

				<PreviousProjectsEditor
					value={previousProjects}
					onChange={setPreviousProjects}
					profileType="contractor"
				/>

				{error && (
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-pill-risk)",
							fontFamily: "var(--font-body)",
						}}
					>
						{error}
					</p>
				)}

				<StudioButton
					onClick={handleSave}
					variant="primary"
					disabled={isSaving}
					icon={Save}
					style={{ width: "100%" }}
				>
					{isSaving ? t("common.saving") : t("common.saveChanges")}
				</StudioButton>
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// REALTOR EDIT FORM
// ============================================================================

function RealtorEditForm() {
	const { data: profile, isPending, isFetching, refetch } = useRealtorProfile();

	if (isPending) return <FormSkeleton />;
	// See the comment on ContractorEditForm above: getMyRealtorProfile resolves
	// successfully with profile: null on a failed request, so isError never
	// fires. A missing profile here means "retry", not "still loading".
	if (!profile) {
		return (
			<QueryErrorState onRetry={() => refetch()} isRetrying={isFetching} />
		);
	}
	return <RealtorEditFormLoaded key={profile.id} profile={profile} />;
}

function RealtorEditFormLoaded({
	profile,
}: {
	profile: Loaded<typeof useRealtorProfile>;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { refreshUser: refreshAuthUser } = useAuthStore();
	const updateProfile = useUpdateRealtorProfile();
	const [error, setError] = useState<string | null>(null);
	const CITIES = useServiceCities().map((c) => c.value);

	const [name, setName] = useState(() => profile.name || "");
	const [agencyName, setAgencyName] = useState(() => profile.agencyName || "");
	const [bio, setBio] = useState(() => profile.bio || "");
	const [experienceYears, setExperienceYears] = useState(() =>
		profile.yearsExperience != null ? String(profile.yearsExperience) : "",
	);
	const [serviceCities, setServiceCities] = useState<string[]>(
		() => profile.serviceCities || [],
	);
	const [profileImageUrl, setProfileImageUrl] = useState(
		() => profile.profileImageUrl || "",
	);
	const [primaryCity, setPrimaryCity] = useState(
		() => profile.primaryCity || "",
	);
	const [propertyTypes, setPropertyTypes] = useState<string[]>(
		() => profile.propertyTypes || [],
	);
	const [clientTypes, setClientTypes] = useState<string[]>(
		() => profile.clientTypes || [],
	);

	const isSaving = updateProfile.isPending;

	const handleSave = async () => {
		setError(null);
		try {
			const result = await updateProfile.mutateAsync({
				name: name.trim(),
				agencyName: agencyName.trim() || null,
				bio: bio.trim() || null,
				yearsExperience: experienceYears ? parseInt(experienceYears, 10) : null,
				serviceCities,
				profileImageUrl: profileImageUrl || null,
				primaryCity: primaryCity || null,
				propertyTypes,
				clientTypes,
			});
			if (result.success) {
				await refreshAuthUser();
				navigate(-1);
			} else {
				setError(result.error || t("profile.failedToSave"));
			}
		} catch {
			setError(t("profile.somethingWentWrong"));
		}
	};

	return (
		<FloatPanel elevation={1} padding={28}>
			<div className="space-y-6">
				<div className="space-y-1">
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 600,
							fontSize: 20,
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("profile.editProfile")}
					</h2>
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-muted)",
							fontFamily: "var(--font-body)",
						}}
					>
						{t("profile.realtorSubtitle")}
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="realtor-name">{t("profile.name")}</Label>
					<Input
						id="realtor-name"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("profile.namePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.profilePhoto")}</Label>
					<SingleImageUpload
						currentUrl={profileImageUrl || undefined}
						onUpload={(url) => setProfileImageUrl(url)}
						onRemove={() => setProfileImageUrl("")}
						enableCrop
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="realtor-agency">
						{t("onboarding.realtor.businessName")}
					</Label>
					<Input
						id="realtor-agency"
						type="text"
						value={agencyName}
						onChange={(e) => setAgencyName(e.target.value)}
						placeholder={t("onboarding.realtor.businessNamePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="realtor-bio">{t("profile.bio")}</Label>
					<Textarea
						id="realtor-bio"
						value={bio}
						onChange={(e) => setBio(e.target.value)}
						placeholder={t("onboarding.realtor.bioPlaceholder")}
						rows={4}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="realtor-experience">
						{t("onboarding.realtor.yearsOfExperience")}
					</Label>
					<Input
						id="realtor-experience"
						type="number"
						value={experienceYears}
						onChange={(e) => setExperienceYears(e.target.value)}
						placeholder="e.g., 8"
						min={0}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="realtor-primary-market">
						{t("onboarding.realtor.primaryMarket")}
					</Label>
					<select
						id="realtor-primary-market"
						value={primaryCity}
						onChange={(e) => setPrimaryCity(e.target.value)}
						className="flex h-9 w-full rounded-md border border-[var(--color-border-default)] bg-dashboard-surface px-3 text-sm text-dashboard-text outline-none"
					>
						<option value="">
							{t("onboarding.realtor.selectPrimaryMarket")}
						</option>
						{[...new Set([...CITIES, ...serviceCities])].map((c) => (
							<option key={c} value={c}>
								{c}
							</option>
						))}
					</select>
				</div>

				<div className="space-y-2">
					<Label>{t("profile.serviceCities")}</Label>
					<ToggleChipGroup>
						{CITIES.map((c) => (
							<ToggleChip
								key={c}
								selected={serviceCities.includes(c)}
								onClick={() => setServiceCities((prev) => toggleItem(prev, c))}
							>
								{c}
							</ToggleChip>
						))}
					</ToggleChipGroup>
				</div>

				<OptionChips
					label={t("onboarding.realtor.propertyTypes")}
					options={realtorPropertyTypeOptions(t)}
					selected={propertyTypes}
					onChange={setPropertyTypes}
				/>

				<OptionChips
					label={t("onboarding.realtor.clientTypes")}
					options={realtorClientTypeOptions(t)}
					selected={clientTypes}
					onChange={setClientTypes}
				/>

				{error && (
					<p
						style={{
							fontSize: 13,
							color: "var(--color-dashboard-pill-risk)",
							fontFamily: "var(--font-body)",
						}}
					>
						{error}
					</p>
				)}

				<StudioButton
					onClick={handleSave}
					variant="primary"
					disabled={isSaving}
					icon={Save}
					style={{ width: "100%" }}
				>
					{isSaving ? t("common.saving") : t("common.saveChanges")}
				</StudioButton>
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function EditProfilePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-2xl space-y-6">
				{/* Top Masthead Panel */}
				<FloatPanel elevation={1} padding={20}>
					<div className="flex items-center gap-3">
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={() => navigate("/settings")}
							icon={ArrowLeft}
						>
							{t("common.back")}
						</StudioButton>
						<h1
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
							}}
						>
							Edit{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								profile
							</span>
							.
						</h1>
					</div>
				</FloatPanel>

				{/* Form by user type */}
				{user?.userType === "homeowner" && <HomeownerEditForm />}
				{user?.userType === "designer" && <DesignerEditForm />}
				{user?.userType === "contractor" && <ContractorEditForm />}
				{user?.userType === "realtor" && <RealtorEditForm />}
				{!user?.userType && (
					<FloatPanel elevation={1} padding={24}>
						<div className="text-center space-y-4">
							<p className="text-muted-foreground">
								{t("profile.unableToDetermineType")}
							</p>
							<StudioButton
								onClick={() => navigate("/settings")}
								variant="quiet"
								size="sm"
							>
								{t("common.goToDashboard")}
							</StudioButton>
						</div>
					</FloatPanel>
				)}
			</div>
		</div>
	);
}
